<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <p class="text-sm text-[#58595b] mb-2.5 text-center">Line Current = Wattage / Voltage</p>
      <div class="font-bold text-center">Fill in two values to determine the third</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Wattage</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.wattage}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.wattage}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.wattage}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.wattage) }"
            v-model="state.wattage"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.wattage, state.wattage)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Voltage</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.voltage}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.voltage}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.voltage}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.voltage) }"
            v-model="state.voltage"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.voltage, state.voltage)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Line Current</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.lineCurrent}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.lineCurrent}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.lineCurrent}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.lineCurrent) }"
            v-model="state.lineCurrent"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.lineCurrent, state.lineCurrent)" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length === 0 }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length < minInputFieldsQty }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import CalculatorsHelper from '@/helpers/global/calculators.helper';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  wattage = 'wattage',
  voltage = 'voltage',
  lineCurrent = 'line-current',
}

const state = reactive({
  wattage: '' as string | number,
  voltage: '' as string | number,
  lineCurrent: '' as string | number,
  inputFields: [] as string[],
  outputFields: [] as string[],
});

const calculatorFieldsList: CalculatorFieldsTypes[] = [
  CalculatorFieldsTypes.wattage,
  CalculatorFieldsTypes.voltage,
  CalculatorFieldsTypes.lineCurrent,
];
const calcType: string = 'single-phase-unit-calculator';
const minInputFieldsQty: number = 2;

const updateInputAndOutputFields = (field: CalculatorFieldsTypes, fieldValue: string | number) => {
  CalculatorsHelper.generateInputAndOutputFields(state.inputFields, state.outputFields, field, fieldValue, calcType, minInputFieldsQty);
  state.inputFields = CalculatorsHelper.getInputFields();
  state.outputFields = CalculatorsHelper.getOutputFields();
};

const setOutputFields = () => {
  calculatorFieldsList.forEach((field) => {
    state.outputFields.push(field);
  });
};

const resetAction = () => {
  state.wattage = '';
  state.voltage = '';
  state.lineCurrent = '';
  state.inputFields = [];
  state.outputFields = [];

  setOutputFields();
};

const calculateAction = () => {
  if (state.inputFields.length === minInputFieldsQty) {
    const wattageValue = Number(state.wattage);
    const voltageValue = Number(state.voltage);
    const lineCurrentValue = Number(state.lineCurrent);

    const firstOutputField = state.outputFields[0];

    switch (firstOutputField) {
      case CalculatorFieldsTypes.wattage:
        state.wattage = GlobalHelper.roundToDecimals(lineCurrentValue * voltageValue, 4);
        break;
      case CalculatorFieldsTypes.voltage:
        state.voltage = GlobalHelper.roundToDecimals(wattageValue / lineCurrentValue, 4);
        break;
      case CalculatorFieldsTypes.lineCurrent:
        state.lineCurrent = GlobalHelper.roundToDecimals(wattageValue / voltageValue, 4);
        break;
    }
  }
};

onMounted(() => {
  setOutputFields();
});
</script>
